import { Box, type BoxProps, Tooltip, Typography } from "@mui/material";
import { forwardRef } from "react";
import { formatTimeRange, type ShiftBand } from "../../utils/calendarUtils";
import { tooltipSlotProps } from "./tooltipSlotProps";

/** shift letters of a pinned day config are painted pink — the one hue no
 *  event status/type uses; no background so it never reads as an event */
const OVERRIDE_COLOR = "#C2185B";

const overrideTooltip = (band: ShiftBand) => (
	<>
		<Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
			Modified day config
		</Typography>
		<Typography variant="caption" component="div">
			Shift {band.label} ·{" "}
			{formatTimeRange(new Date(band.startMs), new Date(band.endMs))}
		</Typography>
	</>
);

type BandProps = BoxProps & { band: ShiftBand; isCurrent: boolean };

// forwardRef so the override's Tooltip can anchor to it
const Band = forwardRef<HTMLDivElement, BandProps>(
	({ band, isCurrent, ...boxProps }, ref) => (
		<Box
			ref={ref}
			{...boxProps}
			sx={{
				position: "absolute",
				left: `${band.leftPct}%`,
				width: `${band.widthPct}%`,
				top: 0,
				bottom: 0,
				...(band.leftPct > 0 && {
					borderLeft: "1px solid #00000033",
				}),
			}}
		>
			<Typography
				variant="caption"
				sx={{
					position: "absolute",
					top: 0,
					left: 2,
					fontSize: band.isOverride ? 10 : 9,
					lineHeight: 1.2,
					color: band.isOverride ? OVERRIDE_COLOR : "text.disabled",
					fontWeight: isCurrent || band.isOverride ? 700 : 400,
				}}
			>
				{band.label}
			</Typography>
		</Box>
	),
);

type Props = {
	bands: ShiftBand[];
	/** highlights the shift containing now; null on the tomorrow board */
	nowMs: number | null;
};

export const ShiftBandsLayer = ({ bands, nowMs }: Props) => (
	<>
		{bands.map((band) => {
			const isCurrent =
				nowMs != null && nowMs >= band.startMs && nowMs < band.endMs;
			const key = `${band.startMs}-${band.leftPct}`;
			// The tooltip is the only explanation of the pink letters
			return band.isOverride ? (
				<Tooltip
					key={key}
					arrow
					slotProps={tooltipSlotProps}
					title={overrideTooltip(band)}
				>
					<Band band={band} isCurrent={isCurrent} />
				</Tooltip>
			) : (
				<Band key={key} band={band} isCurrent={isCurrent} />
			);
		})}
	</>
);
